import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { type Ordine, useDuplicaOrdine } from '../api/ordini.js';
import { Modale } from '../componenti/Modale.js';
import { t } from '../i18n/lingua.js';

/**
 * Duplicare un ordine.
 *
 * Chi inserisce i PO Campbell ne fa decine identiche al giorno, che cambiano
 * solo per il numero. Il v2 chiede tre cose in tre caselle diverse —
 * numerazione progressiva, elenco di numeri, parte fissa — che però sono due:
 * o i numeri si contano avanti, o si dettano.
 *
 * I numeri che verranno si vedono **prima** di premere. È l'unica cosa che
 * risponde alla domanda vera di chi sta lì — «mi escono quelli giusti?» —
 * senza doverlo scoprire dopo aver creato dieci ordini.
 */
export function ModaleDuplicaOrdine({ ordine, onChiudi }: { ordine: Ordine; onChiudi: () => void }) {
  const naviga = useNavigate();
  const duplica = useDuplicaOrdine(ordine.id);

  const [copie, setCopie] = useState(1);
  const [conRighe, setConRighe] = useState(true);
  const [modo, setModo] = useState<'progressive' | 'list'>('progressive');
  const [prefisso, setPrefisso] = useState('');
  const [elenco, setElenco] = useState('');

  const numeri = useMemo(() => {
    if (modo === 'list') {
      return elenco
        .split(',')
        .map((n) => n.trim())
        .filter((n) => n.length > 0);
    }

    // stesso conteggio del servizio, per far vedere prima cosa uscirà: se i
    // due non vanno d'accordo, è il servizio ad avere ragione e lo dice lui
    const resto = ordine.orderNumber.startsWith(prefisso.trim())
      ? ordine.orderNumber.slice(prefisso.trim().length)
      : '';
    if (!/^\d+$/.test(resto)) return [];

    const partenza = Number(resto);
    return Array.from({ length: Math.max(copie, 0) }, (_, i) =>
      prefisso.trim() + String(partenza + i + 1).padStart(resto.length, '0'),
    );
  }, [modo, elenco, ordine.orderNumber, prefisso, copie]);

  const errore = duplica.error instanceof ErroreApi ? duplica.error.message : null;
  const pronto = copie >= 1 && (modo === 'progressive' ? numeri.length > 0 : numeri.length === copie);

  return (
    <Modale
      titolo={t('Duplica l’ordine')}
      occhiello={`${ordine.orderNumber} — ${ordine.customer}`}
      nota={t('Le copie nascono nel primo stato, con un protocollo nuovo. Fatture, navi, container e date della spedizione non si copiano: sono di questo ordine, non del prossimo.')}
      larga
      inCorso={duplica.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={duplica.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            disabled={duplica.isPending || !pronto}
            onClick={() =>
              duplica.mutate(
                {
                  copies: copie,
                  withLines: conRighe,
                  numbering:
                    modo === 'list'
                      ? { mode: 'list', numbers: numeri }
                      : { mode: 'progressive', prefix: prefisso.trim() || null },
                },
                {
                  onSuccess: (esito) => {
                    onChiudi();
                    // una copia sola: si va lì, che è quello che uno voleva.
                    // Dieci: si resta nell'elenco, dove si vedono tutte
                    if (esito.created.length === 1) {
                      void naviga({
                        to: '/sales-orders/$id',
                        params: { id: String(esito.created[0]!.id) },
                      });
                    }
                  },
                },
              )
            }
          >
            {duplica.isPending
              ? t('Duplico…')
              : copie === 1
                ? t('Fai la copia')
                : t('Fai {n} copie', { n: copie })}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="fields">
        <div className="field">
          <label htmlFor="dup-copie">{t('Quante copie')}</label>
          <input
            id="dup-copie"
            className="input"
            type="number"
            min={1}
            max={100}
            value={copie}
            onChange={(e) => setCopie(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
          />
        </div>

        <div className="field">
          <label htmlFor="dup-righe">{t('Le righe')}</label>
          <label className="check" htmlFor="dup-righe">
            <input
              id="dup-righe"
              type="checkbox"
              checked={conRighe}
              onChange={(e) => setConRighe(e.target.checked)}
            />
            <span>
              {ordine.lines.length > 0
                ? t('copia anche le {n} righe', { n: ordine.lines.length })
                : t('questo ordine non ha righe')}
            </span>
          </label>
        </div>

        <div className="field span-2">
          <label htmlFor="dup-modo">{t('I numeri delle copie')}</label>
          <select
            id="dup-modo"
            className="select"
            value={modo}
            onChange={(e) => setModo(e.target.value as 'progressive' | 'list')}
          >
            <option value="progressive">{t('contali avanti da questo')}</option>
            <option value="list">{t('li detto io')}</option>
          </select>
        </div>

        {modo === 'progressive' ? (
          <div className="field span-2">
            <label htmlFor="dup-prefisso">{t('Parte fissa da lasciar stare')}</label>
            <input
              id="dup-prefisso"
              className="input mono"
              placeholder={t('vuoto, se il numero è tutto cifre')}
              value={prefisso}
              onChange={(e) => setPrefisso(e.target.value)}
            />
            {numeri.length === 0 && (
              <p className="dim" style={{ margin: '6px 0 0', fontSize: 12 }}>
                {t(
                  'Da «{numero}» non si sa contare avanti: indica la parte fissa, oppure detta i numeri.',
                  { numero: ordine.orderNumber },
                )}
              </p>
            )}
          </div>
        ) : (
          <div className="field span-2">
            <label htmlFor="dup-elenco">{t('I numeri, separati da una virgola')}</label>
            <input
              id="dup-elenco"
              className="input mono"
              placeholder="900001, 900002, 900003"
              value={elenco}
              onChange={(e) => setElenco(e.target.value)}
            />
            {numeri.length > 0 && numeri.length !== copie && (
              <p className="dim" style={{ margin: '6px 0 0', fontSize: 12 }}>
                {t('Ne hai dettati {dettati} e ne servono {servono}.', {
                  dettati: numeri.length,
                  servono: copie,
                })}
              </p>
            )}
          </div>
        )}

        {numeri.length > 0 && (
          <div className="field span-2">
            <label>{t('Verranno')}</label>
            <p className="mono" style={{ margin: 0, fontSize: 13, lineHeight: 1.7 }}>
              {numeri.slice(0, 12).join(' · ')}
              {numeri.length > 12 && ` … ${t('e altri {n}', { n: numeri.length - 12 })}`}
            </p>
          </div>
        )}
      </div>
    </Modale>
  );
}
